import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { Modal, ReactSelect, Switch } from "../../../../components";

const PaycodesModal = props => {
	const { lang } = global;
	const states = [
		{ label: "Active", value: 1 },
		{ label: "Only active for specific dates", value: 2 },
		{ label: "Inactive", value: 3 },
	];
	const { show, onHide, onSave, paycode, user } = props;

	const [shortCode, setShortcode] = useState("");
	const [description, setDescription] = useState("");
	const [status, setStatus] = useState(1);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [maxEndDate, setMaxEndDate] = useState("");
	const [displayPayCode, setDisplayPayCode] = useState(false);

	useEffect(() => {
		if (paycode) {
			setShortcode(paycode.Name);
			setDescription(paycode.Description);
			setDisplayPayCode(paycode.DisplayPayCode);
			if (!paycode.IsActive) {
				setStatus(3);
			} else if (paycode.PayCodeUseStartDate) {
				setStatus(2);
				let auxStartDate = paycode.PayCodeUseStartDate;
				if (paycode.PayCodeUseStartDate instanceof Date) {
					auxStartDate = paycode.PayCodeUseStartDate.toISOString().slice(0, 19);
				}
				let auxEndDate = paycode.PayCodeUseEndDate;
				if (paycode.PayCodeUseEndDate instanceof Date) {
					auxEndDate = paycode.PayCodeUseEndDate.toISOString().slice(0, 19);
				}
				const cleanStartDate = utils.fixIELocaleString(auxStartDate);
				const formattedStartDate = utils.formatDate(
					utils.isIOSDevice ? cleanStartDate.replace(/-/gi, "/") : cleanStartDate,
				);
				const cleanEndDate = utils.fixIELocaleString(auxEndDate);
				const formattedEndDate = utils.formatDate(
					utils.isIOSDevice ? cleanEndDate.replace(/-/gi, "/") : cleanEndDate,
				);
				setStartDate(formattedStartDate);
				setEndDate(formattedEndDate);
			} else {
				setStatus(1);
			}
		} else {
			setShortcode("");
			setDescription("");
			setStatus(1);
			setStartDate("");
			setEndDate("");
		}
	}, [paycode, show]);

	const shortCodeChange = value => {
		if (value === "") {
			setShortcode(value);
		}
		if (!value.match(/^[0-9a-z ]+$/i) || value.length > 50) {
			return;
		}
		setShortcode(value);
	};

	const descriptionChange = value => {
		if (value === "") {
			setDescription(value);
		}
		if (value.length > 255) {
			return;
		}
		setDescription(value);
	};

	const startDateChange = value => {
		setStartDate(value);
		setMaxEndDate(utils.fixIELocaleString(value).slice(0, 10));
	};

	const formatDates = (startDate1, endDate1, startTime1, endTime1) => {
		// date format must change if user is on iOS
		const formattedStartDate = utils.isIOSDevice
			? startDate1.replace(/-/gi, "/")
			: utils.fixIELocaleString(startDate1);
		const formattedEndDate = utils.isIOSDevice
			? endDate1.replace(/-/gi, "/")
			: utils.fixIELocaleString(endDate1);
		const startDateTime = new Date(
			`${formattedStartDate} ${utils.fixIELocaleString(startTime1)}`,
		);
		const endDateTime = new Date(`${formattedEndDate} ${utils.fixIELocaleString(endTime1)}`);
		// Handle scenario where Date objects may be invalid for some reason
		if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
			return { StartDateTime: "", EndDateTime: "" };
		}

		// If endDateTime earlier than startDateTime, add a day to EndDateTime
		// to indicate job ends the next day
		if (endDateTime < startDateTime) {
			endDateTime.setDate(endDateTime.getDate() + 1);
		}

		const startDateTimeMs = startDateTime.getTime() - startDateTime.getTimezoneOffset() * 60000;
		const endDateTimeMs = endDateTime.getTime() - endDateTime.getTimezoneOffset() * 60000;
		// Format dates
		return {
			StartDateTime: new Date(startDateTimeMs).toISOString().slice(0, -1),
			EndDateTime: new Date(endDateTimeMs).toISOString().slice(0, -1),
		};
	};

	const endDateChange = value => {
		setEndDate(value);
	};

	const validateDisableForm = () => {
		if (shortCode === "" || description === "") {
			return true;
		}
		if (status === 2) {
			if (endDate === "" || startDate === "") {
				return true;
			}
			if (startDate > endDate) {
				return true;
			}
		}
		return false;
	};

	return (
		<Modal
			id="pay-codes-modal"
			dialogClassName="pay-codes-dialog"
			show={show}
			onHide={() => onHide()}
		>
			<Modal.Header
				closeButton
				className="hidden-print"
			>
				<Modal.Title>{paycode ? "Edit Pay Code" : "New Pay Code"}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="container-fluid">
				<div style={{ marginLeft: "15px", marginRight: "15px" }}>
					<div className="row">
						<div className="col-md-4 col-xs-6 status-indicator">
							<small>{lang.t("Display on job listing")}</small>
							<br />
							<Switch
								on={displayPayCode}
								onClick={() => setDisplayPayCode(!displayPayCode)}
							/>
						</div>
					</div>
					<div className="row">
						<br />
						<div className="col-xs-12">{lang.t("Enter short code")}</div>
						<div className="col-xs-12">
							<input
								className="form-control"
								name="shortCode"
								maxLength="50"
								value={shortCode}
								onChange={e => shortCodeChange(e.target.value)}
							/>
						</div>
					</div>
					<div className="row">
						<br />
						<div className="col-xs-12">{lang.t("Enter description")}</div>
						<div className="col-xs-12">
							<input
								className="form-control"
								name="description"
								maxLength="255"
								value={description}
								onChange={e => descriptionChange(e.target.value)}
							/>
						</div>
					</div>
					<div className="row">
						<br />
						<div className="col-xs-12">{lang.t("Pay Code Status")}</div>
						<div className="col-xs-12">
							<ReactSelect
								id="payCodeStatusSelect"
								name="payCodeStatusSelect"
								options={states}
								value={status}
								onChange={e => setStatus(e.value)}
							/>
						</div>
					</div>
					<If condition={status === 2}>
						<div className="row">
							<br />
							<div className="col-xs-12">{lang.t("From")}</div>
							<div className="col-xs-6">
								<input
									name="payCodeStartDate"
									id="payCodeStartDate"
									className="form-control"
									type="date"
									value={startDate}
									onChange={e => startDateChange(e.target.value)}
								/>
							</div>
						</div>
						<div className="row">
							<br />
							<div className="col-xs-12">{lang.t("To")}</div>
							<div className="col-xs-6">
								<input
									name="payCodeEndDate"
									id="payCodeEndDate"
									className="form-control"
									type="date"
									min={maxEndDate}
									value={endDate}
									onChange={e => endDateChange(e.target.value)}
								/>
							</div>
						</div>
					</If>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					id="button-go-back"
					className="btn btn-link hidden-print"
					type="button"
					onClick={() => onHide()}
				>
					{lang.t("Close")}
				</button>
				<button
					id="button-print-table"
					className="btn btn-primary hidden-print"
					type="button"
					disabled={validateDisableForm()}
					onClick={() =>
						onSave({
							Id: paycode ? paycode.Id : undefined,
							OrganizationId: user.Organization.Id,
							Name: shortCode,
							Description: description,
							IsActive: status === 1 || status === 2,
							PayCodeUseStartDate:
								status === 2 && startDate
									? formatDates(startDate, endDate, "00:00", "23:59")
											.StartDateTime
									: null,
							PayCodeUseEndDate:
								status === 2 && endDate
									? formatDates(startDate, endDate, "00:00", "23:59").EndDateTime
									: null,
							DisplayPayCode: displayPayCode,
						})
					}
				>
					{lang.t("Save Changes")}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

PaycodesModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	onSave: PropTypes.func.isRequired,
	user: PropTypes.object.isRequired,
	paycode: PropTypes.object,
};

PaycodesModal.defaultProps = {
	show: false,
	onHide: () => {},
	paycode: null,
};

export default PaycodesModal;
